import { useState, useEffect, useCallback } from 'react';
import { useTestContext } from '../../../context/testContext';
import { ResponsiveEyeSelection, MobileOptimaSelection, Flex, NavButtons } from '../../../2022-component-library';
import { ResponsiveOptimaChartTestOptician } from '.';
import { ResponsiveAcuityResults } from '../';
import { optimaChartData } from '../../../helpers/data';
import { useReceiveMessage, usePostMessage } from '../../../context/messageContext';
import { useResultData } from '../../../helpers/useResultData';

const ResponsiveOptimaChartContainer = ({ setActiveTab, setTestStep }) => {
  const { testData, setTestFieldValue, updateTestData } = useTestContext();
  const { clearCurrentEyeResults } = useResultData();
  const [step, setStep] = useState('eyeSetup');
  const [backStep, setBackStep] = useState('');
  const [canProgress, setCanProgress] = useState(false);
  const [nextStep, setNextStep] = useState('');
  const [selectedOptimaSize, setSelectedOptimaSize] = useState(1);
  const receivedMessage = useReceiveMessage();
  const postMessage = usePostMessage();

  useEffect(() => {
    let selectedEye;
    switch (true) {
      case step === 'eyeSetup':
        setBackStep('selectTest');
        setNextStep(testData.testBothEyes ? 'bothEyesSetup' : testData.testRightEye ? 'rightEyeSetup' : 'leftEyeSetup');
        break;
      case step.endsWith('Setup'):
        setCanProgress(true);
        selectedEye = step.split('Setup')[0];
        if (selectedEye === 'leftEye' && testData.testRightEye) {
          updateTestData({ testInProgress: true });
        } else updateTestData({ testInProgress: false });
        setNextStep(`${selectedEye}Test`);
        setBackStep(selectedEye === 'leftEye' && testData.testRightEye ? 'rightEyeSetup' : 'eyeSetup');
        break;
      case step.endsWith('Test'):
        selectedEye = step.split('Test')[0];
        clearCurrentEyeResults(selectedEye);
        updateTestData({ testInProgress: true });
        setNextStep(selectedEye === 'rightEye' && testData.testLeftEye ? 'leftEyeSetup' : 'results');
        setBackStep(`${selectedEye}Setup`);
        break;
      default:
        break;
    }
  }, [step, testData.testRightEye, testData.testLeftEye, testData.testBothEyes, updateTestData, clearCurrentEyeResults]);

  const updateStep = useCallback((selectedStep) => {
    setCanProgress(false);
    if (selectedStep === 'selectTest') {
      setTestStep('selectTest');
      postMessage({ type: 'holding' });
    } else {
      setBackStep(step);
      if (selectedStep.endsWith('Setup')) postMessage({ type: 'holding' });
      if (selectedStep === 'results') postMessage({ type: 'endOfTest' });
      if (selectedStep.endsWith('Test')) {
        setTestFieldValue({ name: 'startingTextSize', value: optimaChartData.find((optimaSize) => selectedOptimaSize === optimaSize.id) });
        postMessage({
          type: 'optimaChart',
          content: {},
        });
      }
      setStep(selectedStep);
    }
  }, []);

  useEffect(() => {
    if (receivedMessage?.type === 'beginTestAgain') updateStep('eyeSetup');
  }, [receivedMessage, updateStep]);

  const updateTextContents = (textContents) => {
    postMessage({
      type: 'optimaTextContents',
      content: {
        textContents: textContents,
      },
    });
  };

  const renderSwitch = () => {
    switch (step) {
      case 'eyeSetup':
        return <ResponsiveEyeSelection setCanProgress={setCanProgress} />;
      case 'bothEyesSetup':
      case 'leftEyeSetup':
      case 'rightEyeSetup':
        return (
          <MobileOptimaSelection
            updateStep={(step) => updateStep(step)}
            selectedOptimaSize={selectedOptimaSize}
            setSelectedOptimaSize={setSelectedOptimaSize}
            eyeTested={step.split('Setup')[0]}
          />
        );
      case 'leftEyeTest':
      case 'rightEyeTest':
      case 'bothEyesTest':
        return (
          <ResponsiveOptimaChartTestOptician
            setCanProgress={setCanProgress}
            eyeTested={step.split('Test')[0]}
            updateTextContents={updateTextContents}
          />
        );
      case 'results':
        return <ResponsiveAcuityResults optimaTest setActiveTab={setActiveTab} />;
      default:
        return;
    }
  };

  return (
    <Flex column centered>
      {renderSwitch()}
      {step !== 'results' && (
        <NavButtons marginTop='0.5rem' updateStep={(direction) => updateStep(direction === 'back' ? backStep : nextStep)} canProgress={canProgress} />
      )}
    </Flex>
  );
};

export default ResponsiveOptimaChartContainer;
